'use client';

import Link from 'next/link';

/**
 * Visual variant of a bottom-nav badge.
 *
 * - `alert`     — red pill, used for things that call for action (unread
 *                 notifications, disputes, overdue payments, etc.). Reserved
 *                 so red genuinely means "something needs your attention".
 * - `count`     — neutral-dark pill, used for ambient counts (upcoming
 *                 bookings, messages in a folder, etc.). Reads as "here's a
 *                 number", not as an alarm.
 *
 * Default is `alert` to preserve the existing behavior for any caller that
 * doesn't specify a variant (notifications/unread counts were the original
 * use case when this component was introduced).
 */
export type MobileBottomNavBadgeVariant = 'alert' | 'count';

export interface MobileBottomNavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  isActive?: boolean;
  /** Numeric badge value. Hidden when `null`/`undefined` or `<= 0`. */
  badge?: number;
  /**
   * Controls the badge color. Defaults to `'alert'` (red) to preserve the
   * original behavior. Use `'count'` for ambient numeric displays so red is
   * reserved for things that require attention.
   */
  badgeVariant?: MobileBottomNavBadgeVariant;
}

interface MobileBottomNavProps {
  navItems: MobileBottomNavItem[];
}

const badgeVariantClasses: Record<MobileBottomNavBadgeVariant, string> = {
  alert: 'bg-red-500 text-white',
  count: 'bg-neutral-800 text-white'
};

export const MobileBottomNav = ({ navItems }: MobileBottomNavProps) => {
  return (
    <nav
      // h-14 (3.5rem) is the original tab-row height. We extend the nav
      // downward by env(safe-area-inset-bottom) and add matching padding so
      // the icons stay in the original 56px row while the extra strip below
      // (under the iOS home indicator) just renders the nav background. On
      // platforms without an inset the calc resolves back to 3.5rem.
      className="mobile-bottom-nav relative flex-shrink-0 h-[calc(3.5rem+env(safe-area-inset-bottom))] pb-[env(safe-area-inset-bottom)] bg-white border-t border-gray-200 md:hidden shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] [body.hide-mobile-nav_&]:hidden z-50"
    >
      <div className="flex justify-between">
        {navItems.map((item) => {
          const badgeVariant: MobileBottomNavBadgeVariant = item.badgeVariant ?? 'alert';
          const showBadge = item.badge != null && item.badge > 0;
          return (
            <Link
              href={item.href}
              key={item.href}
              className={`flex flex-col items-center justify-center flex-1 py-2 ${item.isActive ? 'text-neutral-900 font-semibold' : 'text-neutral-500'}`}
            >
              {/*
               * Fixed 24×24 icon frame. Every icon passed in uses `size-5`
               * (20×20), so centering them in a slightly larger (24×24) frame
               * with `items-center justify-center` guarantees:
               *
               *   1) Every tab reserves the exact same vertical space for its
               *      icon — so the label below sits on the same baseline
               *      across tabs (the caller's reported alignment issue).
               *   2) Sub-pixel rendering differences between icon sets (some
               *      viewBoxes are 20×20, some 24×24, some 28×28, each with
               *      different internal padding) get absorbed by the 2px
               *      slack on each side rather than nudging the whole column.
               *   3) The badge overlay has a deterministic anchor corner
               *      regardless of whichever icon is inside.
               *
               * Pinning the frame (rather than letting the icon's own intrinsic
               * size drive layout) is the only robust fix here because the
               * icon set is heterogeneous (lucide + custom SVGs) with
               * different internal glyph-to-viewBox ratios — a per-icon tweak
               * would regress every time someone swaps one out.
               */}
              <div className="relative w-6 h-6 flex items-center justify-center">
                {item.icon}
                {showBadge && (
                  <span
                    // Badge sits tight against the frame's top-right corner.
                    // Smaller than before (14×14, text-[9px]) — the previous
                    // 16×16 / text-[10px] pill dominated the icon visually at
                    // mobile sizes; a slightly smaller chip reads as an
                    // annotation rather than a competing element.
                    className={`absolute -top-1 -right-1 min-w-[14px] h-3.5 rounded-full ${badgeVariantClasses[badgeVariant]} text-[9px] font-bold flex items-center justify-center px-1`}
                  >
                    {item.badge! > 99 ? '99+' : item.badge}
                  </span>
                )}
              </div>
              <span className="max-[400px]:text-[10px] text-xs mt-1 text-center leading-none">{item.label}</span>
            </Link>
          );
        })}
      </div>
      <div
        className="absolute top-0 h-0.5 bg-orange-500 transition-all duration-300"
        style={{
          width: `${100 / navItems.length}%`,
          left: `${(navItems.findIndex((item) => item.isActive) / navItems.length) * 100}%`
        }}
      />
    </nav>
  );
};
